import React, {
  useCallback, useMemo, useRef, useState
} from 'react';
import isFunction from 'lodash/isFunction';
import sortBy from 'lodash/sortBy';

import { FormControl, useConfirm } from '@primer/react';
import { PeopleIcon } from '@primer/octicons-react';

import { UserType } from '../../../constants/user.constants';

import Button from '../../common/Button/Button';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';
import SearchInput from '../../common/TextInputs/SearchInput/SearchInput';

import NoUsersSlate from '../../slates/NoUserSlate';
import UserList from '../../users/UserList/UserList';
import WorkspaceInviteForm from '../WorkspaceInviteForm/WorkspaceInviteForm';

import { augmentUserData } from '../../../utils/user_utils';
import { getThemeColor } from '../../../utils/theme-utils';
import { sendWorkspaceInvitations } from '../../../utils/sendEmailInvitations';

import useOnClickOutside from '../../../utils/hooks/use_on_click_outside';
import useUserContext from '../../contexts/UserData/UserData';
import useWorkspaceContext from '../WorkspaceContext/WorkspaceContext';

const filterCollaboratorsBySearchValue = (searchStr = '', users = []) => users.filter((user) => {
  const displayName = user?.email || user?.username;

  if (!searchStr?.length) {
    return true;
  }
  return displayName && displayName?.toLowerCase().includes(searchStr.toLowerCase());
})

const codeUserTypeForSort = userType => userType === UserType.OWNER ? 0 : 1;
const sortCollaborators = (currentUserId, users = []) => sortBy(users, (user) => {
  const userTypeCode = codeUserTypeForSort(user?.userType);
  const currentUserCode = user?.id === currentUserId ? 0 : 1;
  const displayName = user?.email || user?.username;

  // Current User to the top, followed by workspace owners, then alphabetical order
  return [currentUserCode, userTypeCode, displayName];
})

const CollaboratorsListSection = ({
  users,
  ownerId,
  workspaceId,
  currentUserId,
  defaultWorkspaceId,
  error,
  onChangeRoleClick,
  onRemoveUserClick,
  defaultWorkspaceUserId,
}) => {
  if (error) {
    return (
      <ErrorSlate
        errorText=""
        subText="Unable to load workspace users at this time."
      />
    );
  }
  
  if (!users?.length) {
    return <NoUsersSlate />;
  }
  
  return (
    <UserList
      className="mt-2"
      users={users}
      workspaceId={workspaceId}
      workspaceOwnerId={ownerId}
      currentUserId={currentUserId}
      defaultWorkspaceId={defaultWorkspaceId}
      onChangeRoleClick={onChangeRoleClick}
      onRemoveUserClick={onRemoveUserClick}
      defaultWorkspaceUserId={defaultWorkspaceUserId}
      disableScrollFading
    />
  );
}

export const CollaboratorSearchPanel = ({
  users,
  ownerId,
  workspaceId,
  currentUserId,
  defaultWorkspaceId,
  error,
  searchQuery,
  setSearchQuery,
  onChangeRoleClick,
  onRemoveUserClick,
  defaultWorkspaceUserId,
  totalUserCount,
}) => (
  <div>
    <div className="w-full flex flex-grow-2 justify-start items-center h-full">
      {
        totalUserCount > 4 && (
          <SearchInput
            className="h-10 relative flex-grow border border-border-dark rounded mt-2 pl-10 mr-2 bg-white"
            ariaLabel="Search by email"
            placeholder="Search by email"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
          />
        )
      }
    </div>
    <div className="mt-2">
      <CollaboratorsListSection
        users={users}
        defaultWorkspaceUserId={defaultWorkspaceUserId}
        workspaceId={workspaceId}
        ownerId={ownerId}
        currentUserId={currentUserId}
        defaultWorkspaceId={defaultWorkspaceId}
        error={error}
        onChangeRoleClick={onChangeRoleClick}
        onRemoveUserClick={onRemoveUserClick}
      />
    </div>
  </div>
)

export const WorkspaceCollaboratorsView = ({
  currentUser,
  users,
  workspaceUsers,
  defaultWorkspaceUserId,
  ownerId,
  workspaceId,
  defaultWorkspaceId,
  onChangeUserRole,
  onRemoveUser,
  error,
  isLoading = false,
  isAdmin = false,
}) => {
  const inviteMenuRef = useRef(null);
  const [isInviteMenuOpen, setInviteMenuOpen] = useState(false);
  const [invitationError, setInvitationError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useOnClickOutside(inviteMenuRef, () => {
    setInviteMenuOpen(false);
  })

  const currentUserId = currentUser?.id;
  const filteredUsers = sortCollaborators(currentUserId, filterCollaboratorsBySearchValue(searchQuery, workspaceUsers));
  const totalWorkspaceUsers = workspaceUsers?.length;
  const filteredWorkspaceUserCount = filteredUsers?.length;

  const hasChangeRoleFunction = isFunction(onChangeUserRole) && (ownerId === currentUserId || isAdmin);
  const handleChangeRole = useCallback((userID, newRole) => {
    if (hasChangeRoleFunction) {
      onChangeUserRole(userID, newRole);
    }
  }, [hasChangeRoleFunction, onChangeUserRole]);

  const confirm = useConfirm();

  const hasRemovalFunction = isFunction(onRemoveUser) && (ownerId === currentUserId || isAdmin);
  const handleRemoveUser = useCallback(async (userID) => {
    const confirmOptions = {
      title: 'Remove Contributor',
      content: 'Are you sure you want to remove this user as a collaborator from this workspace?',
      confirmButtonContent: 'Delete',
      confirmButtonType: 'danger'
    };

    if (hasRemovalFunction && await confirm(confirmOptions)) {
      onRemoveUser(userID);
    }
  }, [hasRemovalFunction, onRemoveUser, confirm]);

  const handleInviteFormSubmission = useCallback(async (usersToInvite) => {
    if (usersToInvite?.length) {
      sendWorkspaceInvitations(workspaceId, usersToInvite)
        .then(() => setInvitationError(false))
        .catch(() => setInvitationError(true));
      setInviteMenuOpen(false)
    }
  }, [workspaceId]);

  return (
    <div className="relative">
      {
        isLoading && (
          <LoadingSpinner animationKey="workspace-collaborators-loader" useOverlay />
        )
      }
      <CardView className="w-full mt-2">
        <div className="flex items-center">
          <Icon fill={getThemeColor('brand')} icon={PeopleIcon} size={24} />
          <CardHeader className="ml-2">
            Collaborators{totalWorkspaceUsers === filteredWorkspaceUserCount ? ` (${totalWorkspaceUsers})` : ` (${filteredWorkspaceUserCount}/${totalWorkspaceUsers})`}
          </CardHeader>
        </div>
        <CollaboratorSearchPanel
          users={filteredUsers}
          totalUserCount={totalWorkspaceUsers}
          defaultWorkspaceUserId={defaultWorkspaceUserId}
          workspaceId={workspaceId}
          ownerId={ownerId}
          currentUserId={currentUserId}
          defaultWorkspaceId={defaultWorkspaceId}
          error={error}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onChangeRoleClick={handleChangeRole}
          onRemoveUserClick={handleRemoveUser}
        />
        <FormControl>
          <FormControl.Label visuallyHidden>Invite Collaborators</FormControl.Label>
          <Button
            className="w-full"
            variant="primary"
            onClick={() => setInviteMenuOpen(!isInviteMenuOpen)}
          >
            Invite Collaborators
          </Button>
          {
            invitationError && (
              <FormControl.Validation variant="error">
                Something went wrong with one of your workspace invitations.
              </FormControl.Validation>
            )
          }
        </FormControl>
      </CardView>
      {
        isInviteMenuOpen ? (
          <ModalOverlay
            animationKey="workspace-invite-overlay"
            active={isInviteMenuOpen}
            onModalToggle={setInviteMenuOpen}
          >
            <div className="w-1/3 h-1/2" ref={inviteMenuRef}>
              <div className="flex flex-col">
                <div className="mx-4 my-2 w-full flex flex-row-reverse"><Button variant="close" iconSize={32} onClick={() => setInviteMenuOpen(false)} /></div>
                <WorkspaceInviteForm
                  onFormSubmit={handleInviteFormSubmission}
                  users={users}
                  workspaceUsers={workspaceUsers}
                />
              </div>
            </div>
          </ModalOverlay>
        ) : null
      }
    </div>
  );
}

const ContextualCollaboratorsView = () => {
  const { isAdmin, userData, users = []  } = useUserContext();
  const {
    currentWorkspaceId: workspaceId,
    currentWorkspace,
    defaultWorkspace,
    workspaceUsers,
    updateUserRole,
    removeUserFromWorkspace,
    isSelectedWorkspaceLoading,
    error,
  } = useWorkspaceContext();

  const handleUpdateUserRole = useCallback((userId, userType) => {
    updateUserRole(userId, workspaceId, userType);
  }, [updateUserRole, workspaceId])

  const handleRemoveUser = useCallback((userToRemove) => {
    removeUserFromWorkspace(userToRemove, workspaceId);
  }, [removeUserFromWorkspace, workspaceId])

  const augmentedWorkspaceUsers = useMemo(() => augmentUserData(workspaceUsers, users), [users, workspaceUsers])

  const isCurrentDefaultWorkspace = currentWorkspace?.name.includes('Default')

  return (
    <WorkspaceCollaboratorsView
      workspaceId={workspaceId}
      defaultWorkspaceId={defaultWorkspace?.id}
      defaultWorkspaceUserId={isCurrentDefaultWorkspace ? currentWorkspace?.createdBy : null}
      currentUser={userData}
      workspaceUsers={augmentedWorkspaceUsers}
      users={users}
      ownerId={currentWorkspace?.createdBy}
      onChangeUserRole={handleUpdateUserRole}
      onRemoveUser={handleRemoveUser}
      isLoading={isSelectedWorkspaceLoading}
      isAdmin={isAdmin}
      error={error}
    />
  )
}

export default ContextualCollaboratorsView;
